import { useAuth0 } from "@auth0/auth0-react";
import { Link } from "react-router-dom";
import PricingPage from "./PricingPage";
import { useRef, useEffect, useState } from "react";
import posthog from "posthog-js";
import { 
	LuSparkles as Sparkles, 
	LuTarget as Target, 
	LuGlobe as Globe, 
	LuScale as Scale, 
	LuRocket as Rocket, 
	LuZap as Zap, 
	LuLanguages as Languages, 
	LuPalette as Palette,
	LuBot as Bot,
	LuActivity as BarChart3,
	LuCheck as CheckCircle2,
	LuSearch as Search,
	LuSave as Save,
	LuWand as Wand2,
	LuShare2 as Share2,
	LuLock as Lock,
	LuTrendingUp as TrendingUp,
	LuLightbulb as Lightbulb,
	LuUsers as Users,
	LuInfo as AlertTriangle
} from "react-icons/lu";

export default function LandingPage() {

	const { isAuthenticated } = useAuth0();
	const featuresRef = useRef<HTMLDivElement>(null);
	const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

	useEffect(() => {
		if (window.location.hash === "#features" && featuresRef.current) {
			featuresRef.current.scrollIntoView({ behavior: "smooth" });
		}
	}, []);

	return (
		<header className="min-h-screen bg-white text-gray-800">
			{/* Navbar */}
		<nav className="w-full py-4 px-6 flex justify-between items-center shadow-sm">
			<div className="flex font-bold">
				<a href="/" className="-m-1.5 p-1.5">
					<span className="text-2xl">🥷</span> Namin8
				</a>
			</div>

			<div className="hidden lg:flex lg:gap-x-12">
				<a 
					href="#product" 
					onClick={() => {
						posthog.capture('nav_menu_clicked', { section: 'product' });
					}}
					className="text-sm/6 font-semibold text-gray-900"
				>
					Product
				</a>
				<a
					href="#features"
					onClick={() => {
						posthog.capture('nav_menu_clicked', { section: 'features' });
					}}
					className="text-sm/6 font-semibold text-gray-900"
				>
					Features
				</a>
				<a
					href="/blog"
					onClick={() => {
						posthog.capture('nav_menu_clicked', { section: 'blog' });
					}}
					className="text-sm/6 font-semibold text-gray-900"
				>
					Blog
				</a>
			</div>

		<div className="flex items-center gap-4">
			{/* Get Started / Login Button */}
			<Link
				to="/app"
				className="hidden sm:inline-block bg-indigo-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-indigo-700 transition text-sm"
			>
				{isAuthenticated ? 'Go to App' : 'Get Started Free'}
			</Link>
			
			<button
				className="lg:hidden text-2xl p-2 hover:bg-gray-100 rounded"
				onClick={() => setMobileMenuOpen(true)}
				aria-label="Open navigation menu"
			>
				☰
			</button>
		</div>
		</nav>

		{/* Mobile Menu Overlay */}
		{mobileMenuOpen && (
			<div className="fixed inset-0 z-50 lg:hidden">
				{/* Backdrop */}
				<div 
					className="absolute inset-0 bg-black bg-opacity-50"
					onClick={() => setMobileMenuOpen(false)}
				></div>
				
				{/* Menu Panel */}
				<div className="absolute inset-0 bg-white flex flex-col">
					{/* Header with close button */}
					<div className="flex justify-between items-center p-6 border-b">
						<span className="text-xl font-bold">🥷 Namin8</span>
						<button
							onClick={() => setMobileMenuOpen(false)}
							className="text-3xl p-2 hover:bg-gray-100 rounded"
							aria-label="Close navigation menu"
						>
							×
						</button>
					</div>
					
					{/* Navigation Links */}
					<div className="flex flex-col p-6 space-y-4">
						<a 
							href="#product" 
							className="text-lg font-semibold text-gray-900 py-3 hover:text-indigo-600"
							onClick={() => {
								posthog.capture('nav_menu_clicked', { section: 'product', device: 'mobile' });
								setMobileMenuOpen(false);
							}}
						>
							Product
						</a>
						<a 
							href="#features" 
							className="text-lg font-semibold text-gray-900 py-3 hover:text-indigo-600"
							onClick={() => {
								posthog.capture('nav_menu_clicked', { section: 'features', device: 'mobile' });
								setMobileMenuOpen(false);
							}}
						>
							Features
						</a>
						<a 
							href="#pricing" 
							className="text-lg font-semibold text-gray-900 py-3 hover:text-indigo-600"
							onClick={() => {
								posthog.capture('nav_menu_clicked', { section: 'pricing', device: 'mobile' });
								setMobileMenuOpen(false);
							}}
						>
							Pricing
						</a>
						<a 
							href="/blog" 
							className="text-lg font-semibold text-gray-900 py-3 hover:text-indigo-600"
							onClick={() => {
								posthog.capture('nav_menu_clicked', { section: 'blog', device: 'mobile' });
								setMobileMenuOpen(false);
							}}
						>
							Blog
						</a>
						<Link
							to="/app"
							className="bg-indigo-600 text-white px-6 py-3 rounded-lg font-semibold text-center hover:bg-indigo-700 transition mt-4"
							onClick={() => {
								posthog.capture('cta_launch_app_clicked', { location: 'mobile_menu' });
								setMobileMenuOpen(false);
							}}
						>
							Get Started Free
						</Link>
					</div>
				</div>
			</div>
		)}

			<main>
		{/* Hero */}
		<section className="relative text-center py-20 px-6 bg-gradient-to-br from-blue-50 via-white to-indigo-50 min-h-[600px] flex items-center justify-center">
			<div className="max-w-6xl mx-auto">
				<h1 className="text-5xl md:text-6xl font-extrabold mb-6 tracking-tight text-gray-900">
					Your Perfect Brand Name, Verified & Ready
				</h1>
				<p className="text-gray-700 text-xl max-w-3xl mx-auto mb-16 leading-relaxed">
					AI-powered naming with legal verification and trademark research—all in one place
				</p>
				
				{/* Use Case Cards */}
				<div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
					{/* Use Case 1: Generate Brand Name */}
					<div className="bg-white p-8 rounded-2xl border border-gray-200 shadow-sm hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
						<div className="inline-flex items-center justify-center w-16 h-16 bg-indigo-100 rounded-xl mb-5">
							<Sparkles className="w-8 h-8 text-indigo-600" />
						</div>
						<h3 className="text-2xl font-bold mb-3 text-gray-900">Generate Brand Name</h3>
						<p className="text-gray-600 leading-relaxed">
							AI creates unique, memorable brand names tailored to your vision with BRAND FIT scoring
						</p>
					</div>

					{/* Use Case 2: Domain Check */}
					<div className="bg-white p-8 rounded-2xl border border-gray-200 shadow-sm hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
						<div className="inline-flex items-center justify-center w-16 h-16 bg-emerald-100 rounded-xl mb-5">
							<Globe className="w-8 h-8 text-emerald-600" />
						</div>
						<h3 className="text-2xl font-bold mb-3 text-gray-900">Domain Check</h3>
						<p className="text-gray-600 leading-relaxed">
							Instantly verify domain availability across .com, .io, .ai and more extensions
						</p>
					</div>

					{/* Use Case 3: Trademark Check */}
					<div className="bg-white p-8 rounded-2xl border border-gray-200 shadow-sm hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
						<div className="inline-flex items-center justify-center w-16 h-16 bg-amber-100 rounded-xl mb-5">
							<Search className="w-8 h-8 text-amber-600" />
						</div>
						<h3 className="text-2xl font-bold mb-3 text-gray-900">Trademark Research</h3>
						<p className="text-gray-600 leading-relaxed">
							Search USPTO and international trademark databases to avoid legal conflicts before you commit
						</p>
					</div>
				</div>

				{/* CTA Button */}
				<div className="text-center">
					<Link
						to="/app"
						onClick={() => {
							posthog.capture('hero_cta_clicked', { location: 'hero_section' });
						}}
						className="inline-block bg-indigo-600 text-white px-10 py-4 rounded-xl font-bold hover:bg-indigo-700 transition shadow-lg text-lg"
					>
						Get Started Free
					</Link>
				</div>
			</div>
		</section>

				{/* BRAND FIT Framework Section */}
				<section className="bg-gradient-to-b from-indigo-50 to-white py-20 px-6">
					<div className="max-w-6xl mx-auto">
						<div className="text-center mb-12">
							<h2 className="text-4xl font-bold mb-4 text-gray-900">
								The BRAND FIT Framework
							</h2>
							<p className="text-xl text-gray-600 max-w-3xl mx-auto">
								Stop guessing. Start knowing. Our proprietary 8-dimension scoring system evaluates every name 
								on a scale of 0-40, giving you scientific confidence in your brand choice.
							</p>
						</div>

						<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
							<BrandFitDimension 
								icon={<Sparkles className="w-10 h-10 text-indigo-600" />}
								title="Brandability" 
								desc="Is it memorable, distinct, and truly ownable? We measure if it sticks in minds."
							/>
							<BrandFitDimension 
								icon={<Target className="w-10 h-10 text-indigo-600" />}
								title="Relevance" 
								desc="Does it align with your industry and convey the right meaning to your audience?"
							/>
							<BrandFitDimension 
								icon={<Globe className="w-10 h-10 text-indigo-600" />}
								title="Availability" 
								desc="Can you actually use it? We check domains and trademark databases."
							/>
							<BrandFitDimension 
								icon={<Scale className="w-10 h-10 text-indigo-600" />}
								title="Neutrality" 
								desc="No hidden baggage. We flag potential negative associations or conflicts."
							/>
							<BrandFitDimension 
								icon={<Rocket className="w-10 h-10 text-indigo-600" />}
								title="Differentiation" 
								desc="Stand out from the crowd. Does it set you apart from competitors?"
							/>
							<BrandFitDimension 
								icon={<Zap className="w-10 h-10 text-indigo-600" />}
								title="Feel" 
								desc="The vibe matters. Does it evoke the right emotions and resonate with your brand?"
							/>
							<BrandFitDimension 
								icon={<Languages className="w-10 h-10 text-indigo-600" />}
								title="Internationality" 
								desc="Think global. Is it pronounceable and appropriate across markets?"
							/>
							<BrandFitDimension 
								icon={<Palette className="w-10 h-10 text-indigo-600" />}
								title="Technical Aesthetics" 
								desc="Visual appeal and phonetic flow. Does it look and sound professional?"
							/>
						</div>

					</div>
				</section>

				{/* Screenshots Section */}
				{/* <section className="bg-white py-20 px-6">
					<div className="max-w-5xl mx-auto text-center">
						<h2 className="text-3xl font-bold mb-8">📸 What it looks like</h2>
						<div className="grid md:grid-cols-3 gap-6">
							<Screenshot
								src="/screenshots/generator.png"
								alt="Name Generator Input"
								caption="Enter your keywords and choose style"
							/>
							<Screenshot
								src="/screenshots/results.png"
								alt="Generated Names"
								caption="Get AI-generated names + domain availability"
							/>
							<Screenshot
								src="/screenshots/favorites.png"
								alt="Favorites & Export"
								caption="Save your favorites and export anytime"
							/>
						</div>
					</div>
				</section> */}

				<section className="space-y-8 py-20 px-6 bg-white">
					<div className="max-w-5xl mx-auto text-center">
						<h2 className="text-4xl font-bold mb-4">How It Works</h2>
						<p className="text-xl text-gray-600 mb-12 max-w-2xl mx-auto">
							From zero to validated brand name in three simple steps
						</p>
						<div className="grid md:grid-cols-3 gap-8 py-6 text-left">
							<Step 
								number="1" 
								title="Describe Your Vision" 
								desc="Share your startup idea or keywords. Our AI generates dozens of unique, brandable names tailored to your industry and tone." 
							/>
							<Step 
								number="2" 
								title="BRAND FIT Analysis" 
								desc="Every name gets scored across 8 scientific dimensions (0-40 scale). See exactly why a name works—or doesn't." 
							/>
							<Step 
								number="3" 
								title="Validate & Launch" 
								desc="Check trademarks, verify domain availability, and confidently choose your winner. Export and share with your team." 
							/>
						</div>
					</div>
				</section>


				{/* Features */}
				<section id="features" className="bg-gray-50 py-20 px-6">
					<div className="max-w-6xl mx-auto">
						<div className="text-center mb-12">
							<h2 className="text-4xl font-bold mb-4">Everything You Need to Find & Validate Your Brand</h2>
							<p className="text-xl text-gray-600 max-w-2xl mx-auto">
								More than a name generator—a complete brand validation toolkit
							</p>
						</div>
						<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 text-left">
							<Feature 
								icon={<Bot className="w-8 h-8 text-indigo-600" />}
								title="AI-Powered Generation" 
								desc="Create dozens of unique, brandable names instantly. Control tone, style, and length to match your vision." 
								status="live"
							/>
							<Feature 
								icon={<BarChart3 className="w-8 h-8 text-indigo-600" />}
								title="BRAND FIT Score (8 Dimensions)" 
								desc="Scientific evaluation across brandability, relevance, availability, and more. Get a 0-40 score with detailed rationale." 
								status="live"
							/>
							<Feature 
								icon={<CheckCircle2 className="w-8 h-8 text-indigo-600" />}
								title="Real-Time Domain Check" 
								desc="Instantly verify .com, .io, .ai and more. Know what's available before you fall in love with a name." 
								status="live"
							/>
							<Feature 
								icon={<Save className="w-8 h-8 text-indigo-600" />}
								title="Save & Compare Names" 
								desc="Organize favorites, compare names side-by-side, and make data-driven decisions with your team." 
								status="live"
							/>
							<Feature 
								icon={<Search className="w-8 h-8 text-indigo-600" />}
								title="Trademark Search (USPTO & EU)" 
								desc="Check USPTO and international trademark databases to avoid costly legal conflicts. Launch with confidence." 
								status="coming-soon"
							/>
							<Feature 
								icon={<Wand2 className="w-8 h-8 text-indigo-600" />}
								title="AI Brand Identity" 
								desc="Generate compelling taglines and positioning statements that bring your brand story to life." 
								status="coming-soon"
							/>
							<Feature 
								icon={<Share2 className="w-8 h-8 text-indigo-600" />}
								title="Export & Team Sharing" 
								desc="Download results as CSV or share session links with co-founders and stakeholders." 
								status="coming-soon"
							/>
							<Feature 
								icon={<Lock className="w-8 h-8 text-indigo-600" />}
								title="Cloud Sync" 
								desc="Log in to save your naming sessions and access them from any device, anytime." 
								status="coming-soon"
							/>
						</div>
					</div>
				</section>

				{/* Why BRAND FIT Matters */}
				<section className="bg-white py-20 px-6">
					<div className="max-w-4xl mx-auto">
						<div className="text-center mb-12">
							<h2 className="text-4xl font-bold mb-6 text-gray-900">
								Why You Can't Just "Pick a Cool Name"
							</h2>
							<p className="text-xl text-gray-600 leading-relaxed">
								Choosing a brand name is one of the most critical decisions you'll make. Get it wrong, 
								and you risk trademark disputes, poor memorability, or negative cultural associations.
							</p>
						</div>

						<div className="grid md:grid-cols-3 gap-8 mb-12">
							<div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-lg">
								<div className="flex items-center gap-2 mb-3">
									<AlertTriangle className="w-5 h-5 text-red-600" />
									<h3 className="font-bold text-lg text-red-900">Trademark Conflicts</h3>
								</div>
								<p className="text-gray-700 text-sm">
									Launch with an existing trademark and face costly legal battles or forced rebrand. 
									Check before you commit.
								</p>
							</div>
							<div className="bg-orange-50 border-l-4 border-orange-500 p-6 rounded-r-lg">
								<div className="flex items-center gap-2 mb-3">
									<AlertTriangle className="w-5 h-5 text-orange-600" />
									<h3 className="font-bold text-lg text-orange-900">Poor Memorability</h3>
								</div>
								<p className="text-gray-700 text-sm">
									A forgettable name means wasted marketing dollars. Your customers won't remember you, 
									and you'll struggle to build word-of-mouth.
								</p>
							</div>
							<div className="bg-yellow-50 border-l-4 border-yellow-500 p-6 rounded-r-lg">
								<div className="flex items-center gap-2 mb-3">
									<AlertTriangle className="w-5 h-5 text-yellow-600" />
									<h3 className="font-bold text-lg text-yellow-900">Cultural Issues</h3>
								</div>
								<p className="text-gray-700 text-sm">
									What sounds great in English might be offensive or awkward in other languages. 
									Think global from day one.
								</p>
							</div>
						</div>

						<div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-2xl p-10 text-center">
							<h3 className="text-3xl font-bold mb-4">The Solution: BRAND FIT</h3>
							<p className="text-xl mb-6 max-w-2xl mx-auto leading-relaxed">
								Don't guess. Know. Our 8-dimension framework removes the guesswork and gives you 
								scientific confidence in your brand decision. Every name gets evaluated on 40+ data points.
							</p>
							<Link
								to="/app"
								onClick={() => {
									posthog.capture('cta_try_now_clicked', { location: 'why_brandfit_section' });
								}}
								className="inline-block bg-white text-indigo-600 px-8 py-4 rounded-lg font-bold hover:bg-gray-100 transition shadow-lg text-lg"
							>
								Try It Free Now
							</Link>
						</div>
					</div>
				</section>

				{/* Use Cases / Social Proof */}
				<section className="bg-gray-50 py-20 px-6">
					<div className="max-w-6xl mx-auto">
						<div className="text-center mb-12">
							<h2 className="text-4xl font-bold mb-4">Built for Ambitious Builders</h2>
							<p className="text-xl text-gray-600">
								Whether you're launching your first startup or your fifth, Namin8 helps you move faster
							</p>
						</div>

						<div className="grid md:grid-cols-3 gap-8">
							<UseCase
								icon={<TrendingUp className="w-12 h-12 text-indigo-600" />}
								title="Startup Founders"
								desc="Validate your brand name before incorporation. Check trademarks, domains, and get BRAND FIT scores to pitch investors with confidence."
								benefit="Save weeks of back-and-forth"
							/>
							<UseCase
								icon={<Lightbulb className="w-12 h-12 text-indigo-600" />}
								title="Side Project Creators"
								desc="Launch your indie hack with a memorable name that stands out. Get domain-ready names in minutes, not days."
								benefit="Ship faster, brand smarter"
							/>
							<UseCase
								icon={<Users className="w-12 h-12 text-indigo-600" />}
								title="Rebrand Teams"
								desc="Compare naming options scientifically. Use BRAND FIT scores to make data-driven decisions and align stakeholders."
								benefit="End the endless debates"
							/>
						</div>
					</div>
				</section>

				{/* Pricing */}
				<section id="pricing" className="bg-white py-20 px-6">
					<PricingPage />
				</section>

			</main>

			{/* Footer */}
			<footer className="bg-gray-900 text-gray-300 py-12 px-6">
				<div className="max-w-6xl mx-auto">
					<div className="grid md:grid-cols-4 gap-8 mb-8">
						<div>
							<h3 className="text-white font-bold text-lg mb-4">🥷 Namin8</h3>
							<p className="text-sm text-gray-400 leading-relaxed">
								Scientific brand naming for ambitious builders. Generate, evaluate, and validate with BRAND FIT.
							</p>
						</div>
						<div>
							<h4 className="text-white font-semibold mb-4">Product</h4>
							<ul className="space-y-2 text-sm">
								<li><a href="/app" className="hover:text-white transition">Name Generator</a></li>
								<li><a href="/app" className="hover:text-white transition">BRAND FIT Scoring</a></li>
								<li><a href="/app" className="hover:text-white transition">USPTO Trademark Search</a></li>
								<li><a href="#features" className="hover:text-white transition">Features</a></li>
								<li><a href="#pricing" className="hover:text-white transition">Pricing</a></li>
							</ul>
						</div>
						<div>
							<h4 className="text-white font-semibold mb-4">Resources</h4>
							<ul className="space-y-2 text-sm">
								<li><a href="/blog" className="hover:text-white transition">Blog</a></li>
								<li><a href="/startup-name-generator" className="hover:text-white transition">Startup Name Generator</a></li>
								<li><a href="/blog" className="hover:text-white transition">How to Name Your Startup</a></li>
							</ul>
						</div>
						<div>
							<h4 className="text-white font-semibold mb-4">Company</h4>
							<ul className="space-y-2 text-sm">
								<li><a href="/contact" className="hover:text-white transition">Contact</a></li>
								<li><a href="/privacy" className="hover:text-white transition">Privacy Policy</a></li>
								<li><a href="/terms" className="hover:text-white transition">Terms of Service</a></li>
							</ul>
						</div>
					</div>
					<div className="border-t border-gray-800 pt-8 text-center text-sm text-gray-500">
						<p>© {new Date().getFullYear()} Namin8 – Brand Name Generator | Trademark Research | Company Naming Tool</p>
						<p className="mt-2">Helping founders find perfect brand names since 2024</p>
					</div>
				</div>
			</footer>
		</header >
	);
}

const Feature = ({
	icon,
	title,
	desc,
	status = "live",
}: {
	icon: React.ReactNode;
	title: string;
	desc: string;
	status?: "live" | "coming-soon";
}) => (
	<div className="flex gap-4 items-start">
		<div className="flex-shrink-0">{icon}</div>
		<div className="flex-1">
			<div className="flex items-center gap-2 mb-1">
				<h3 className="font-semibold text-lg">{title}</h3>
				{status === "live" ? (
					<span className="bg-green-100 text-green-700 text-xs font-semibold px-2 py-1 rounded">
						Live
					</span>
				) : (
					<span className="bg-amber-100 text-amber-700 text-xs font-semibold px-2 py-1 rounded">
						Coming Soon
					</span>
				)}
			</div>
			<p className="text-gray-600 text-sm">{desc}</p>
		</div>
	</div>
);

function Step({ number, title, desc }: { number: string; title: string; desc: string }) {
	return (
		<div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 flex flex-col gap-3 text-center">
			<div className="text-4xl font-bold text-indigo-600">{number}</div>
			<div className="font-bold text-xl">{title}</div>
			<div className="text-gray-600 leading-relaxed">{desc}</div>
		</div>
	);
}

function BrandFitDimension({ icon, title, desc }: { icon: React.ReactNode; title: string; desc: string }) {
	return (
		<div className="bg-white p-6 rounded-xl shadow-sm border border-indigo-100 hover:shadow-md transition-shadow">
			<div className="mb-3">{icon}</div>
			<h3 className="font-bold text-lg mb-2 text-gray-900">{title}</h3>
			<p className="text-sm text-gray-600 leading-relaxed">{desc}</p>
		</div>
	);
}

function UseCase({ icon, title, desc, benefit }: { icon: React.ReactNode; title: string; desc: string; benefit: string }) {
	return (
		<div className="bg-white p-8 rounded-xl shadow-md border border-gray-100 hover:shadow-lg transition-shadow">
			<div className="mb-4">{icon}</div>
			<h3 className="font-bold text-2xl mb-3 text-gray-900">{title}</h3>
			<p className="text-gray-600 mb-4 leading-relaxed">{desc}</p>
			<div className="bg-indigo-50 text-indigo-700 px-4 py-2 rounded-lg text-sm font-semibold inline-flex items-center gap-2">
				<CheckCircle2 className="w-4 h-4" /> {benefit}
			</div>
		</div>
	);
}
